import * as React from "react";
import { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { useTranslation } from "react-i18next";
import {
  UpfrontPaymentAnswerWrapper,
  UpfrontPaymentComponentContentWrapper,
  UpfrontPaymentHelpGeneralContainer,
  UpfrontPaymentHelpWrapper,
  UpfrontPaymentInfoBillContent,
  UpfrontPaymentMainText,
  UpfrontPaymentPageWrapper,
  UpfrontPaymentSolidLine,
  UpfrontPaymentTitle,
} from "./UpfrontPayment.styles";
import { UpfrontPaymentMain } from "./UpfrontPaymentMain";
import { GetTypographyContent } from "../shared/Typography/Typography";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { UpfrontPaymentReceipt } from "./UpfrontPaymentReceipt";
import { UpfrontPaymentReceiptView } from "./UpfrontPaymentReceiptEmail";
import { UpfrontPaymentBillingSummary } from "./UpfrontPaymentBillingSummary";
import { UpfrontPaymentInsurance } from "./UpfrontPaymentInsurance";
import { FinancialAssistanceModal } from "./FinancialAssistance/FinancialAssistanceModal";
import { UpfrontPaymentStripe } from "./UpfrontPaymentStripe";
import {
  BillingSupportEmail,
  BillingSupportNumber,
  StepperIndex,
} from "./types";
import {
  Bill,
  GetUpfrontPayment,
  UpfrontPaymentPlan,
  getCentsToDollars,
  GetSavedCardsUpfront,
  GetCurrentPaymentMethodForPaymentPlanUpfront,
  showUpfrontPayment,
} from "./Services/UpfrontPaymentService";

import { Accordions } from "../shared/Accordion/Accordions";

import { getAllRequisitions, getUserAuth } from "../../recoil/selectors";
import { BlankHeader } from "../Header/BlankHeader";
import { UpfrontPaymentPlanBillingSummary } from "./UpfrontPaymentPlanBillingSummary";
import { patientProfileState, patientState } from "../../recoil/atoms";
import { BILLING_MULTIPLE_URL, LOGIN_PAGE_URL } from "../../utils/urlConstants";
import { billingHelpAccordion } from "../../pages/Help/BillingHelpData";
import FAQTitle from "../FAQ/FAQSectionTitle";
import BillingVideoEmbed from "../shared/Video/BillingVideoEmbed";
import { ChevronLeft } from "../../assets/iconComponents/ChevronLeft";
import { BabyPeekPaymentPlanSteps } from "../BabyPeek/BabyPeekPaymentPlanSteps";

import {
  BabyPeekPaymentDropdownGridPaymentWrapper,
  BabyPeekPaymentPlanGrid,
  BabyPeekStripeIconPointGridText,
  BabyPeekPaymentPlanScheduleWrapper,
  BabyPeekPaymentSummaryTitle,
} from "../BabyPeek/BabyPeekStripe.styles";
import LightDarkDescription from "../BabyPeek/Traits/LightOrDarkHair/LightDarkDescription.png";
import {
  GetBabyPeekPaymentPlan,
  SavedCardType,
} from "../BabyPeek/Services/BabyPeekService";
import { VideoFeedBackTitleText } from "../shared/Video/VideoEmbed.styles";
import { ChatWidget } from "../../pages/ContactUs/ChatWidget";
import { TalkdeskChatWidget } from "../TalkdeskChatWidget/TalkdeskChatWidget";
import { ErrorPopup } from "../shared/ErrorPopup/ErrorPopup";
import {
  savedPaymentMethodsErrorMessage,
  currentPaymentMethodErrorMessage,
} from "../../utils/helpers";
import { SpinnerLoading } from "../shared/Spinner/SpinnerLoading";
import { useVariantFeatureFlag } from "../../utils/hooks/useFeatureFlag";
import { FeatureFlags } from "../../utils/launchDarklyUtils";
import { BillingLinkVerification } from "./BillingLinkVerification";
import { useBillingLinkAuthorization } from "./useBillingLinkAuthorization";
import { removeCookieAccessToken } from "../../utils/requestUtils";

const PUBLIC_PATIENT_PORTAL_URL = "https://results.unityscreen.com/";

export const UpfrontPayment: React.FC = () => {
  const { deviceType } = useDeviceHook();
  const { t } = useTranslation();
  const billingFaqAccordions = billingHelpAccordion(t, false);
  const { variant: chatbotVariant } = useVariantFeatureFlag(
    FeatureFlags.PatientPortalChatbot,
  );
  const history = useHistory();
  const [activeStep, setActiveStep] = useState(StepperIndex.Main);
  const [paymentModalOpen, setPaymentModalOpen] = useState<boolean>(false);
  const [stripeModalOpen, setStripeModalOpen] = useState<boolean>(false);
  const setPatientReqState = useSetRecoilState(patientState);
  const setPatientProfileState = useSetRecoilState(patientProfileState);
  const auth = useRecoilValue(getUserAuth);
  const [upfrontBill, setUpfrontBill] = useState<Bill>();
  const [isLoadingBill, setIsLoadingBill] = useState(true);
  const [paymentSubmitted, setPaymentSubmitted] = useState(false);
  const { billingPK, adminID } = useParams<{
    billingPK: string;
    adminID?: string;
  }>();
  const {
    authorizationState,
    markAuthorized,
    markAuthorizationError,
    retryAuthorization,
    recoverFromUnauthorizedResponse,
  } = useBillingLinkAuthorization({
    billingPK,
    accessToken: auth?.access_token,
  });
  const [paymentPlanSteps, setPaymentPlanSteps] =
    useState<UpfrontPaymentPlan[]>();
  const [savedPayments, setSavedPayments] = useState<SavedCardType[]>();
  const [
    currentPaymentMethodForPaymentPlan,
    setCurrentPaymentMethodForPaymentPlan,
  ] = useState<SavedCardType>();

  const preserveBillingReturnPath = () => {
    sessionStorage.setItem(
      "current-pathname",
      `${history.location.pathname}${history.location.search}`,
    );
  };

  const signOutAndUseAnotherAccount = () => {
    preserveBillingReturnPath();
    removeCookieAccessToken();
    setPatientReqState(undefined);
    setPatientProfileState(undefined);
    history.replace(LOGIN_PAGE_URL);
  };
  const [useSavedPayment, setUsedSavePayment] = useState<string>();
  const [useNewCard, setUseNewCard] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const requisitions = useRecoilValue(getAllRequisitions);
  const billingReqs = requisitions?.filter((req) =>
    showUpfrontPayment(req.portal_bill_status),
  );

  useEffect(() => {
    const getSavedCards = async () => {
      if (authorizationState === "authorized" && auth) {
        try {
          const savedCardsStepsResponse = await GetSavedCardsUpfront(
            billingPK,
            auth?.access_token,
          );
          if (savedCardsStepsResponse?.ok) {
            const savedCardsStepsResponseStepsJson =
              await savedCardsStepsResponse.json();
            setSavedPayments(savedCardsStepsResponseStepsJson.payment_methods);
          } else {
            setErrorMessage(savedPaymentMethodsErrorMessage);
          }
        } catch (error) {
          setErrorMessage(savedPaymentMethodsErrorMessage);
        }
      }
    };

    getSavedCards();
  }, [auth, authorizationState, billingPK]);

  useEffect(() => {
    const getCurrentPaymentMethodForPaymentPlan = async () => {
      try {
        const currentPaymentMethodResponse =
          await GetCurrentPaymentMethodForPaymentPlanUpfront(billingPK);
        if (currentPaymentMethodResponse?.ok) {
          const currentPaymentMethodJson =
            await currentPaymentMethodResponse.json();
          const currentPaymentMethod =
            currentPaymentMethodJson.payment_methods?.[0];
          setCurrentPaymentMethodForPaymentPlan(currentPaymentMethod);
        } else {
          setErrorMessage(currentPaymentMethodErrorMessage);
        }
      } catch (error) {
        setErrorMessage(currentPaymentMethodErrorMessage);
      }
    };

    if (upfrontBill?.is_payment_plan_enabled) {
      getCurrentPaymentMethodForPaymentPlan();
    }
  }, [upfrontBill?.is_payment_plan_enabled]);

  const loadAuthorizedBill = React.useCallback(
    async (signal?: AbortSignal): Promise<Bill | undefined> => {
      try {
        const response = await GetUpfrontPayment(billingPK, adminID, signal);
        if (signal?.aborted) {
          return undefined;
        }
        if (response.ok) {
          return (await response.json()) as Bill;
        }
        if (response.status === 401 || response.status === 403) {
          await recoverFromUnauthorizedResponse(signal);
        } else {
          markAuthorizationError();
        }
      } catch {
        if (!signal?.aborted) {
          markAuthorizationError();
        }
      }
      return undefined;
    },
    [
      adminID,
      billingPK,
      markAuthorizationError,
      recoverFromUnauthorizedResponse,
    ],
  );

  const setStripeAndRefresh = async (open: boolean) => {
    const bill = await loadAuthorizedBill();
    if (bill) {
      setUpfrontBill(bill);
    }
    setStripeModalOpen(Boolean(bill) && open);
  };

  useEffect(() => {
    if (authorizationState !== "authorized") {
      return;
    }

    const controller = new AbortController();
    const getUpfrontBill = async () => {
      setIsLoadingBill(true);
      const bill = await loadAuthorizedBill(controller.signal);
      if (controller.signal.aborted) {
        return;
      }
      if (bill) {
        setUpfrontBill(bill);
      }
      setIsLoadingBill(false);
    };
    getUpfrontBill();
    return () => controller.abort();
  }, [authorizationState, loadAuthorizedBill]);

  const billReflectsSubmittedPayment = Boolean(
    upfrontBill?.portal_bill_status === "paid_in_full" ||
    upfrontBill?.portal_bill_status === "refunded" ||
    upfrontBill?.is_payment_plan_enabled,
  );

  useEffect(() => {
    if (billReflectsSubmittedPayment) {
      setPaymentSubmitted(true);
    }
  }, [billReflectsSubmittedPayment]);

  useEffect(() => {
    const filteredSavedPayments = savedPayments?.filter(
      (saved) => saved.is_chargeable,
    );
    if (filteredSavedPayments?.length && !useNewCard && !useSavedPayment) {
      setUsedSavePayment(filteredSavedPayments[0].payment_method_id);
    } else if (filteredSavedPayments || !auth) {
      setUseNewCard(true);
    }
  }, [savedPayments]);

  useEffect(() => {
    const getPaymentPlanSteps = async () => {
      if (upfrontBill?.babypeek_pk) {
        const paymentPlanStepsResponse = await GetBabyPeekPaymentPlan(
          upfrontBill?.babypeek_pk,
          auth?.access_token,
        );
        const paymentPlanStepsJson = await paymentPlanStepsResponse.json();
        setPaymentPlanSteps(paymentPlanStepsJson);
      }
    };

    getPaymentPlanSteps();
  }, [upfrontBill]);

  useEffect(() => {
    if (!billReflectsSubmittedPayment) {
      return;
    }

    setPatientReqState((currentPatientState) => {
      if (
        !currentPatientState?.requisitions.some(
          (req) => req.bill_pk === upfrontBill?.pk,
        )
      ) {
        return currentPatientState;
      }

      return {
        ...currentPatientState,
        requisitions: currentPatientState.requisitions.map((req) =>
          req.bill_pk === upfrontBill?.pk
            ? {
                ...req,
                portal_bill_status:
                  upfrontBill?.portal_bill_status ?? "paid_in_full",
              }
            : req,
        ),
      };
    });
  }, [
    billReflectsSubmittedPayment,
    setPatientReqState,
    upfrontBill?.pk,
    upfrontBill?.portal_bill_status,
  ]);

  function getStepContent(step: number) {
    window.scrollTo(0, 0);
    switch (step) {
      case StepperIndex.Main:
        return (
          <>
            {upfrontBill && (
              <UpfrontPaymentMain
                setStep={setActiveStep}
                setPaymentOpen={setPaymentModalOpen}
                setStripeOpen={setStripeAndRefresh}
                paymentSubmitted={paymentSubmitted}
                upfrontBill={upfrontBill}
                setUpfrontBill={setUpfrontBill}
                setHomeScreen={() => setActiveStep(StepperIndex.Main)}
                savedPayments={savedPayments}
                currentPaymentMethodForPaymentPlan={
                  currentPaymentMethodForPaymentPlan
                }
                setSavedPayments={setSavedPayments}
                setCurrentPaymentMethodForPaymentPlan={
                  setCurrentPaymentMethodForPaymentPlan
                }
                setUseNewCard={setUseNewCard}
                setUsedSavePayment={setUsedSavePayment}
                useNewCard={useNewCard}
                useSavedPayment={useSavedPayment}
              />
            )}
          </>
        );
      case StepperIndex.Receipt:
        return (
          <>
            {upfrontBill && (
              <>
                <UpfrontPaymentReceipt
                  isPaymentPlan={
                    StepperIndex.PaymentPlan === activeStep ||
                    upfrontBill.is_payment_plan_enabled
                  }
                  paymentAmount={
                    upfrontBill.start_of_payment_amount || upfrontBill.balance
                  }
                  setActiveStep={setActiveStep}
                />
                <UpfrontPaymentReceiptView />{" "}
              </>
            )}
          </>
        );
      case StepperIndex.ItemizedBill:
        return (
          <>
            {upfrontBill && (
              <UpfrontPaymentBillingSummary
                setPaymentOpen={setPaymentModalOpen}
                setStripeOpen={setStripeAndRefresh}
                upfrontBill={upfrontBill}
                setStep={setActiveStep}
              />
            )}
          </>
        );
      case StepperIndex.Insurance:
        return (
          <>
            {" "}
            {upfrontBill && (
              <UpfrontPaymentInsurance
                setUpfrontBill={setUpfrontBill}
                upfrontBill={upfrontBill}
                setHomeScreen={() => setActiveStep(StepperIndex.Main)}
              />
            )}
          </>
        );
      case StepperIndex.PaymentPlan:
        return (
          <>
            {upfrontBill && (
              <UpfrontPaymentPlanBillingSummary
                setPaymentOpen={setPaymentModalOpen}
                setStripeOpen={setStripeAndRefresh}
                setStep={setActiveStep}
                upfrontBill={upfrontBill}
                savedPayments={savedPayments}
                setUseNewCard={setUseNewCard}
                setUsedSavePayment={setUsedSavePayment}
                useNewCard={useNewCard}
                useSavedPayment={useSavedPayment}
                setSavedPayments={setSavedPayments}
              />
            )}
          </>
        );
      default:
        return "unknown step";
    }
  }

  const multiReqSupport =
    activeStep === StepperIndex.Main &&
    billingReqs &&
    billingReqs.length &&
    billingReqs.length > 1;

  if (authorizationState === "checking") {
    return <SpinnerLoading />;
  }

  if (authorizationState !== "authorized") {
    return (
      <>
        {!auth?.access_token && (
          <BlankHeader link={PUBLIC_PATIENT_PORTAL_URL} />
        )}
        <BillingLinkVerification
          billingPK={billingPK}
          mode={authorizationState}
          onAuthorized={markAuthorized}
          onRetry={retryAuthorization}
          onSignIn={preserveBillingReturnPath}
          onSignOut={signOutAndUseAnotherAccount}
        />
      </>
    );
  }

  if (isLoadingBill) {
    return <SpinnerLoading />;
  }

  return (
    <>
      {errorMessage && (
        <ErrorPopup
          message={errorMessage}
          hideMessage={() => setErrorMessage("")}
        />
      )}
      {upfrontBill ? (
        <>
          {!auth?.access_token && (
            <BlankHeader link={PUBLIC_PATIENT_PORTAL_URL} />
          )}
          <UpfrontPaymentPageWrapper
            deviceType={deviceType}
            style={{ pointerEvents: stripeModalOpen ? "none" : undefined }}
          >
            <UpfrontPaymentComponentContentWrapper>
              <FinancialAssistanceModal
                financialAssistanceModalOpen={paymentModalOpen}
                setFinancialAssistanceModalOpen={setPaymentModalOpen}
                setUpfrontBill={setUpfrontBill}
                setStripeModalOpen={setStripeModalOpen}
              />
              {upfrontBill && (
                <UpfrontPaymentStripe
                  setUpfrontBill={setUpfrontBill}
                  setStripeModalOpen={setStripeModalOpen}
                  stripeModalOpen={stripeModalOpen}
                  clientSecret={upfrontBill?.client_secret}
                  paymentAmount={
                    upfrontBill.start_of_payment_amount || upfrontBill.balance
                  }
                  setPaymentSubmitted={setPaymentSubmitted}
                  paymentSubmitted={paymentSubmitted}
                  currentStep={activeStep}
                  setActiveStep={setActiveStep}
                  useSavedPayment={useSavedPayment}
                  upfrontPayment={upfrontBill}
                  useNewCard={useNewCard}
                />
              )}
              <UpfrontPaymentTitle
                pointer={multiReqSupport || activeStep !== StepperIndex.Main}
                onClick={() => {
                  if (multiReqSupport) {
                    return history.push(BILLING_MULTIPLE_URL);
                  }

                  return activeStep === StepperIndex.Main
                    ? undefined
                    : setActiveStep(StepperIndex.Main);
                }}
              >
                {(activeStep !== StepperIndex.Main || multiReqSupport) && (
                  <ChevronLeft />
                )}{" "}
                {multiReqSupport ? `View all bills` : `Billing`}
              </UpfrontPaymentTitle>
              {getStepContent(activeStep)}
              {paymentPlanSteps && paymentPlanSteps.length > 0 && (
                <BabyPeekPaymentDropdownGridPaymentWrapper
                  deviceType={deviceType}
                  style={{
                    marginTop: "24px",
                    width: deviceType !== "mobile" ? "532px" : "auto",
                    justifySelf: deviceType !== "mobile" ? "center" : "",
                  }}
                >
                  <BabyPeekPaymentPlanGrid>
                    <img
                      src={LightDarkDescription}
                      style={{ maxWidth: "200px" }}
                      alt="LightDarkDescription"
                    />
                    <BabyPeekPaymentSummaryTitle>
                      BabyPeek
                    </BabyPeekPaymentSummaryTitle>

                    <BabyPeekPaymentPlanScheduleWrapper>
                      <BabyPeekStripeIconPointGridText
                        style={{ fontWeight: 600 }}
                      >
                        Payment Schedule
                      </BabyPeekStripeIconPointGridText>
                      {paymentPlanSteps && (
                        <BabyPeekPaymentPlanSteps steps={paymentPlanSteps} />
                      )}
                      <UpfrontPaymentSolidLine />
                      <UpfrontPaymentInfoBillContent
                        style={{ marginTop: "10px" }}
                      >
                        <UpfrontPaymentMainText>
                          {t("upfrontPayment.paymentPlan.noInterest")}
                        </UpfrontPaymentMainText>
                        <UpfrontPaymentMainText>
                          {t("upfrontPayment.paymentPlan.totalAmount", {
                            amount: getCentsToDollars(
                              paymentPlanSteps?.reduce(
                                (acc, curr) => acc + curr.amount,
                                0,
                              ),
                            ),
                          })}
                        </UpfrontPaymentMainText>
                      </UpfrontPaymentInfoBillContent>
                    </BabyPeekPaymentPlanScheduleWrapper>
                  </BabyPeekPaymentPlanGrid>
                </BabyPeekPaymentDropdownGridPaymentWrapper>
              )}
            </UpfrontPaymentComponentContentWrapper>

            <div>
              {GetTypographyContent({
                content: t("help.title"),
                size: "h2",
                isBold: true,
                padding: "0 0 16px 16px",
              })}
              <UpfrontPaymentHelpGeneralContainer
                padding="0px"
                paddingBottom="10px"
                style={{ marginBottom: 0 }}
              >
                <FAQTitle title={t("help.sections.billingFaq")} />
                <UpfrontPaymentHelpWrapper>
                  <VideoFeedBackTitleText style={{ paddingLeft: "16px" }}>
                    {t("help.billingVideoTitle")}
                  </VideoFeedBackTitleText>
                  <BillingVideoEmbed
                    reqID={upfrontBill?.req_identifier ?? ""}
                    videoLocation="billing_page"
                    trackingEnabled={Boolean(
                      auth?.access_token && upfrontBill?.req_identifier,
                    )}
                  />
                </UpfrontPaymentHelpWrapper>
                <Accordions
                  data={billingFaqAccordions}
                  isLinked={false}
                  size="large"
                />
              </UpfrontPaymentHelpGeneralContainer>
              <UpfrontPaymentHelpGeneralContainer
                padding="20px 24px"
                style={{
                  marginTop: "16px",
                  textAlign: "center",
                  boxSizing: "border-box",
                }}
              >
                {GetTypographyContent({
                  content: t("upfrontPayment.help.stillHaveQuestions"),
                  size: "h3",
                  isBold: true,
                  padding: "0 0 8px 0",
                })}
                <UpfrontPaymentAnswerWrapper
                  deviceType={deviceType}
                  style={{ width: "100%", margin: 0 }}
                >
                  {GetTypographyContent({
                    content: t(
                      "upfrontPayment.multipleBills.billingQuestionsPrefix",
                    ),
                    size: "medium",
                  })}
                  {GetTypographyContent({
                    content: BillingSupportNumber,
                    size: "medium",
                    isPhone: true,
                    textColor: "greenDark",
                  })}
                  {GetTypographyContent({
                    content: t(
                      "upfrontPayment.multipleBills.billingQuestionsMiddle",
                    ),
                    size: "medium",
                  })}
                  {GetTypographyContent({
                    content: BillingSupportEmail,
                    size: "medium",
                    isEmail: true,
                    textColor: "greenDark",
                  })}
                  {GetTypographyContent({
                    content: t("upfrontPayment.help.billingQuestionsSuffix"),
                    size: "medium",
                  })}
                </UpfrontPaymentAnswerWrapper>
              </UpfrontPaymentHelpGeneralContainer>
            </div>
            {chatbotVariant === "dialpad" && <ChatWidget />}
            {chatbotVariant === "talkdesk" && <TalkdeskChatWidget />}
          </UpfrontPaymentPageWrapper>
        </>
      ) : (
        GetTypographyContent({
          content: t("upfrontPayment.errors.loadingPage"),
          size: "medium",
        })
      )}
    </>
  );
};
